import { keepPreviousData, useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router";
import { PageHeader } from "@/components/PageHeader";
import { SearchInput } from "@/components/SearchInput";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Avatar } from "@/features/history/HistoryEventRow";
import { RunGroup, SingleEvent } from "@/features/history/RunGroup";
import { get } from "@/lib/api";
import { clock, localDate, today } from "@/lib/format";
import { historyQuery, readHistoryFilters, writeHistoryFilters, type HistoryFilters, type HistoryWho } from "@/lib/filters";
import { AREAS, displayName, groupHistory } from "@/lib/history";
import { dayHeading, relativeDays } from "@/lib/relative";
import type { HistoryActor, HistoryPage } from "@/lib/types";
import { cn } from "@/lib/utils";

const AREA_KEYS = Object.keys(AREAS);
const WHO: [HistoryWho, string][] = [["all", "Alle"], ["user", "Ich"], ["agent", "Agents"], ["system", "Zeitplan & Import"]];

function lastSeen(lastAt: string | null): string {
  if (!lastAt) return "noch keine Änderung";
  const day = localDate(lastAt);
  return day === today() ? `zuletzt heute, ${clock(lastAt)}` : `zuletzt ${relativeDays(day, today())}`;
}

function Access({ actors }: { actors: HistoryActor[] | undefined }) {
  return (
    <section aria-labelledby="h-access" className="space-y-3 rounded-lg border bg-card p-4">
      <h2 id="h-access" className="text-[0.9375rem] font-semibold">Wer darf ändern</h2>
      {!actors && Array.from({ length: 2 }, (_, i) => <div key={i} className="h-9 animate-pulse rounded-md bg-muted motion-reduce:animate-none" />)}
      {actors?.map((a) => (
        <div key={a.name} className="flex items-center gap-2.5">
          <Avatar actor={a} />
          <span className="flex min-w-0 flex-col text-[0.8125rem]">
            <span className="truncate text-sm font-medium">{displayName(a)}</span>
            <span className="truncate text-muted-foreground">{a.kind === "user" ? "Web-App mit Passkey · CLI" : `Token „${a.name}“`}</span>
            <span className="truncate text-muted-foreground tabular-nums">{lastSeen(a.lastAt)}</span>
          </span>
        </div>
      ))}
    </section>
  );
}

function Skeleton() {
  return (
    <div className="overflow-hidden rounded-lg border bg-card" aria-hidden>
      {Array.from({ length: 6 }, (_, i) => (
        <div key={i} className="flex items-center gap-3 border-t px-4 py-3.5 first:border-t-0">
          <div className="size-7 shrink-0 animate-pulse rounded-full bg-muted motion-reduce:animate-none" />
          <div className="h-3.5 animate-pulse rounded bg-muted motion-reduce:animate-none" style={{ width: `${50 + ((i * 17) % 40)}%` }} />
        </div>
      ))}
    </div>
  );
}

export function Verlauf() {
  const [sp, setSp] = useSearchParams();
  const f = readHistoryFilters(sp, AREA_KEYS);
  const setF = (patch: Partial<HistoryFilters>) => setSp(writeHistoryFilters({ ...f, ...patch }), { replace: true });

  // The search runs on the server: wait for a pause in typing before asking it.
  const [q, setQ] = useState(f.q);
  useEffect(() => {
    const t = setTimeout(() => setQ(f.q), 250);
    return () => clearTimeout(t);
  }, [f.q]);

  const history = useInfiniteQuery({
    queryKey: ["history", f.who, f.area, q],
    queryFn: ({ pageParam }) => get<HistoryPage>(historyQuery({ ...f, q }, pageParam)),
    initialPageParam: undefined as number | undefined,
    getNextPageParam: (last) => last.nextBefore ?? undefined,
    placeholderData: keepPreviousData,
  });
  const actors = useQuery({ queryKey: ["history", "actors"], queryFn: () => get<{ actors: HistoryActor[] }>("/api/history/actors") });

  const events = history.data?.pages.flatMap((p) => p.events) ?? [];
  const days = groupHistory(events);
  const now = new Date();

  return (
    <div className="grid items-start gap-6 min-[900px]:grid-cols-[minmax(0,1fr)_18.75rem]">
      <div className="min-w-0 space-y-4">
        <div className="space-y-1.5">
          <PageHeader title="Verlauf" />
          <p className="text-sm text-pretty text-muted-foreground">
            Jede Änderung, egal ob von dir in der Web-App, vom Agent über MCP, aus der Menüleiste oder vom Zeitplan. Einträge lassen sich nicht ändern oder löschen.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <div role="group" aria-label="Wer" className="flex flex-wrap gap-1.5">
            {WHO.map(([who, label]) => (
              <button
                key={who}
                type="button"
                aria-pressed={f.who === who}
                onClick={() => setF({ who })}
                className={cn(
                  "inline-flex h-8 items-center rounded-full border px-3 text-[0.8125rem] whitespace-nowrap transition-colors duration-150 outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 motion-reduce:transition-none pointer-coarse:h-11",
                  f.who === who ? "border-primary bg-primary text-primary-foreground" : "bg-card hover:bg-accent",
                )}
              >
                {label}
              </button>
            ))}
          </div>
          <Select value={f.area ?? "all"} onValueChange={(v) => setF({ area: v === "all" ? null : v })}>
            <SelectTrigger size="sm" aria-label="Bereich" className="sm:ml-auto pointer-coarse:h-11"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle Bereiche</SelectItem>
              {AREA_KEYS.map((k) => <SelectItem key={k} value={k}>{AREAS[k]}</SelectItem>)}
            </SelectContent>
          </Select>
          <SearchInput className="w-full sm:w-56 [&_input]:h-8 pointer-coarse:[&_input]:h-11" value={f.q} onChange={(q) => setF({ q })} placeholder="RE-1086, Fritsch, Railway" />
        </div>

        {history.isError && <p className="text-sm text-destructive">{history.error.message}</p>}
        {history.isPending && <Skeleton />}
        {history.isSuccess && events.length === 0 && (
          <p className="rounded-lg border bg-card p-6 text-center text-sm text-muted-foreground">Keine Änderungen für diesen Filter.</p>
        )}

        <div
          aria-busy={history.isPlaceholderData}
          className={cn("space-y-6 transition-opacity duration-150 motion-reduce:transition-none", history.isPlaceholderData && "opacity-60")}
        >
          {days.map((d) => (
            <section key={d.day} aria-labelledby={`tag-${d.day}`} className="space-y-2">
              <h2 id={`tag-${d.day}`} className="text-[0.8125rem] font-semibold text-muted-foreground">{dayHeading(d.day, now)}</h2>
              {d.groups.map((g) => g.events.length > 1
                ? <RunGroup key={g.key} events={g.events} />
                : <SingleEvent key={g.key} event={g.events[0]!} />)}
            </section>
          ))}
        </div>

        {history.hasNextPage && (
          <Button variant="outline" size="sm" className="pointer-coarse:h-11" disabled={history.isFetchingNextPage} onClick={() => history.fetchNextPage()}>
            Ältere laden
          </Button>
        )}
      </div>

      <aside aria-label="Zugänge" className="space-y-4">
        {actors.isError ? <p className="text-sm text-destructive">{actors.error.message}</p> : <Access actors={actors.data?.actors} />}
        <section aria-labelledby="h-rules" className="space-y-2 rounded-lg border bg-card p-4">
          <h2 id="h-rules" className="text-[0.9375rem] font-semibold">Nur mit deinem OK</h2>
          <p className="text-[0.8125rem] leading-normal text-pretty text-muted-foreground">
            Senden, Festschreiben, Zahlung buchen, Stornieren und Löschen kann ein Agent nur anfragen. Freigeben kannst nur du, in der Web-App unter „Braucht dich“. Im Verlauf steht dann beides: wer es angefragt und wer es freigegeben hat.
          </p>
        </section>
      </aside>
    </div>
  );
}
